import type { JSX } from 'react';
import type {
  ClientResource,
  CreatePricingRuleBody,
  CreateUseCaseBody,
  PricingRuleBodyInput,
} from '@majuscule/contracts';
import { assertNever } from '@majuscule/domain';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import { formatMarginShare, useSmsServiceCost } from './shared';
import { SmsSimulator } from '../SmsSimulator';
import { CallingCodesField, SenderField } from '../SmsSettingsInputs';
import { useToast } from '../toast';
import { ApiHttpError, internalSend } from '../../lib/api';
import { formatTariffPrice } from '../../lib/tariffs';
import {
  type AiUseCaseKind,
  CATEGORY_LABELS,
  DEFAULT_CALLING_CODES,
  EURO_AMOUNT_PATTERN,
  euroInputToMicroEur,
  EXECUTION_MODE_LABELS,
  type ExecutionMode,
  formatMicroEur,
  isSmsService,
  type Model,
  type PricingRule,
  readSmsSettings,
  suggestSender,
  type UseCase,
  type UseCaseCategory,
} from '../../lib/use-cases';
import { Callout } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
  Stepper,
} from '@/components/ui/dialog';
import { CheckRow, Field, FieldError, Fields2, Fieldset, InputGroup } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { KvList, KvRow } from '@/components/ui/kv';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tag } from '@/components/ui/tag';

/** Translates the API error of a use-case creation into a French message. */
function createUseCaseErrorMessage(error: unknown): string {
  if (error instanceof ApiHttpError) {
    switch (error.status) {
      case 409:
        return 'Ce client possède déjà un cas d’usage avec cette clé.';
      case 404:
        return 'Modèle inconnu.';
      case 422:
        // The API explains the broken rule (sender, codes, model…) in French.
        return error.detail ?? 'Cas d’usage invalide : un client propriétaire est obligatoire.';
      default:
        return 'Échec de la création du cas d’usage.';
    }
  }
  return 'Échec de la création du cas d’usage.';
}

/** Title and description of the creation dialog's second step, per category. */
const CREATE_STEPS: Record<UseCaseCategory, { title: string; description: string }> = {
  ai: {
    title: 'Nouveau cas d’usage IA',
    description: 'Configuration du modèle et des paramètres de génération.',
  },
  communication: {
    title: 'Nouveau cas d’usage Communication',
    description: 'Configuration du canal et du prix de vente.',
  },
};

interface CreateUseCaseCommand {
  body: CreateUseCaseBody;
  /** Default rule created right after the use case (the selling price of an SMS), if any. */
  defaultRule: PricingRuleBodyInput | null;
}

/** A selectable category of the creation dialog's first step (a button, outside any label). */
function CategoryOption({
  title,
  description,
  tags,
  onSelect,
}: {
  title: string;
  description: string;
  tags?: JSX.Element;
  onSelect: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-start justify-between gap-3 rounded-lg border border-border bg-card p-4 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <span className="grid min-w-0 gap-1">
        <span className="text-[13px] font-semibold leading-[18px]">{title}</span>
        <span className="text-caption text-muted-foreground">{description}</span>
        {tags !== undefined ? <span className="mt-1 flex flex-wrap gap-1.5">{tags}</span> : null}
      </span>
      <ChevronRight aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
    </button>
  );
}

/** Purchase cost of the chosen SMS service and margin at the typed price. */
function SmsCostLines({ modelId, price }: { modelId: string; price: string }): JSX.Element | null {
  const cost = useSmsServiceCost(modelId);
  switch (cost.status) {
    case 'none':
    case 'loading':
      return null;
    case 'missing':
      return (
        <Callout variant="warning">
          Ce service n’a pas de tarif en vigueur : ajoutez-en un dans Catalogue, sinon chaque envoi
          échouera.
        </Callout>
      );
    case 'ok': {
      const priceMicroEur = euroInputToMicroEur(price);
      const margin =
        priceMicroEur !== null && cost.costMicroEur !== null
          ? priceMicroEur - cost.costMicroEur
          : null;
      return (
        <KvList>
          <KvRow label="Coût d’achat actuel" mono>
            {formatTariffPrice(cost.tariff.perMessage, cost.tariff.currency)} par SMS
          </KvRow>
          {margin !== null && priceMicroEur !== null ? (
            <KvRow label="Marge par SMS" mono>
              {formatMicroEur(margin)} ({formatMarginShare(margin, priceMicroEur)})
            </KvRow>
          ) : null}
        </KvList>
      );
    }
    default:
      return assertNever(cost);
  }
}

/**
 * Use-case creation in two steps (lot 17, D-SMS.8): the category, then the form
 * of that category. Mounted only while open, so closing it resets everything.
 */
export function CreateUseCaseDialog({
  clients,
  models,
  onClose,
  onCreated,
}: {
  clients: readonly ClientResource[];
  models: readonly Model[];
  onClose: () => void;
  onCreated: (useCaseId: string) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { notify } = useToast();

  // null: first step, the category is not chosen yet.
  const [category, setCategory] = useState<UseCaseCategory | null>(null);
  const [key, setKey] = useState('');
  const [ownerClientId, setOwnerClientId] = useState('');
  const [modelId, setModelId] = useState('');
  // AI form.
  const [kind, setKind] = useState<AiUseCaseKind>('text');
  const [acceptsFiles, setAcceptsFiles] = useState(false);
  const [executionMode, setExecutionMode] = useState<ExecutionMode>('realtime');
  const [maxOutputTokens, setMaxOutputTokens] = useState('');
  const [temperature, setTemperature] = useState('');
  // Communication form.
  const [sender, setSender] = useState('');
  // Once the operator types a sender, choosing another client no longer replaces it.
  const [senderEdited, setSenderEdited] = useState(false);
  const [callingCodes, setCallingCodes] = useState(DEFAULT_CALLING_CODES);
  const [smsPrice, setSmsPrice] = useState('');
  const [formError, setFormError] = useState('');

  const aiModels = models.filter((model) => !isSmsService(model));
  const smsServices = models.filter(isSmsService);
  const clientNameOf = (clientId: string): string =>
    clients.find((client) => client.id === clientId)?.name ?? '';

  const chooseCategory = (next: UseCaseCategory): void => {
    setCategory(next);
    setFormError('');
    // A select whose value matches none of its options shows the first one while
    // the state keeps the stale id: never carry a model over to another category.
    switch (next) {
      case 'ai':
        setModelId('');
        return;
      case 'communication': {
        const only = smsServices.length === 1 ? smsServices[0] : undefined;
        setModelId(only?.id ?? '');
        if (!senderEdited) {
          setSender(suggestSender(clientNameOf(ownerClientId)));
        }
        return;
      }
      default:
        return assertNever(next);
    }
  };

  const back = (): void => {
    setCategory(null);
    setFormError('');
  };

  const changeOwner = (clientId: string): void => {
    setOwnerClientId(clientId);
    if (!senderEdited) {
      setSender(suggestSender(clientNameOf(clientId)));
    }
  };

  const changeSender = (value: string): void => {
    setSender(value);
    // Emptied, the field follows the client again.
    setSenderEdited(value.trim().length > 0);
  };

  const createUseCase = useMutation({
    mutationFn: async (
      command: CreateUseCaseCommand,
    ): Promise<{ useCase: UseCase; priceMissing: boolean }> => {
      const created = await internalSend<UseCase>('POST', '/internal/use-cases', command.body);
      if (command.defaultRule === null) {
        return { useCase: created, priceMissing: false };
      }
      const rule: CreatePricingRuleBody = {
        clientId: null,
        // In effect right away, dated 10 minutes back: absorbs a browser clock ahead of
        // the server. The use case did not exist before, so nothing can be priced earlier.
        effectiveFrom: new Date(Date.now() - 10 * 60_000).toISOString(),
        rule: command.defaultRule,
      };
      try {
        await internalSend<PricingRule>(
          'POST',
          `/internal/use-cases/${created.id}/pricing-rules`,
          rule,
        );
        return { useCase: created, priceMissing: false };
      } catch {
        // The use case exists: report the missing price rather than a failed creation.
        return { useCase: created, priceMissing: true };
      }
    },
    onSuccess: ({ useCase, priceMissing }) => {
      void queryClient.invalidateQueries({ queryKey: ['use-cases'] });
      void queryClient.invalidateQueries({ queryKey: ['pricing-rules', useCase.id] });
      notify(
        priceMissing
          ? 'Cas d’usage créé, mais son prix n’a pas été enregistré : ajoutez une règle de prix'
          : 'Cas d’usage créé',
      );
      onCreated(useCase.id);
    },
    onError: (error) => {
      setFormError(createUseCaseErrorMessage(error));
    },
  });

  const submitAi = (): void => {
    if (key.trim().length === 0) {
      setFormError('La clé est obligatoire.');
      return;
    }
    if (modelId.length === 0) {
      setFormError('Le modèle est obligatoire.');
      return;
    }
    if (ownerClientId.length === 0) {
      setFormError('Un client propriétaire est obligatoire.');
      return;
    }
    if (maxOutputTokens.trim().length > 0) {
      const parsed = Number(maxOutputTokens);
      if (!Number.isInteger(parsed) || parsed <= 0) {
        setFormError('maxOutputTokens doit être un entier positif.');
        return;
      }
    }
    if (temperature.trim().length > 0) {
      const parsed = Number(temperature);
      if (Number.isNaN(parsed) || parsed < 0 || parsed > 2) {
        setFormError('temperature doit être comprise entre 0 et 2.');
        return;
      }
    }
    const parsedMax = maxOutputTokens.trim().length === 0 ? null : Number(maxOutputTokens);
    const parsedTemp = temperature.trim().length === 0 ? null : Number(temperature);
    createUseCase.mutate({
      body: {
        key: key.trim(),
        kind,
        acceptsFiles,
        ownerClientId,
        modelId,
        params: { maxOutputTokens: parsedMax, temperature: parsedTemp },
        executionMode,
      },
      defaultRule: null,
    });
  };

  const submitCommunication = (): void => {
    if (key.trim().length === 0) {
      setFormError('La clé est obligatoire.');
      return;
    }
    if (ownerClientId.length === 0) {
      setFormError('Un client propriétaire est obligatoire.');
      return;
    }
    if (modelId.length === 0) {
      setFormError('Le service SMS est obligatoire.');
      return;
    }
    const settings = readSmsSettings(sender, callingCodes);
    if (!settings.ok) {
      setFormError(settings.error);
      return;
    }
    const price = smsPrice.trim();
    if (!EURO_AMOUNT_PATTERN.test(price)) {
      setFormError('Prix de vente par SMS invalide (euros, ex. 0,09 → 0.09).');
      return;
    }
    // An SMS use case is always realtime, without files nor generation parameters:
    // the contract variant has no such fields.
    createUseCase.mutate({
      body: { key: key.trim(), kind: 'sms', ownerClientId, modelId, sms: settings.value },
      defaultRule: { type: 'per_unit', unit: 'sms', amount: price },
    });
  };

  const submit = (): void => {
    setFormError('');
    switch (category) {
      case null:
        return;
      case 'ai':
        submitAi();
        return;
      case 'communication':
        submitCommunication();
        return;
      default:
        return assertNever(category);
    }
  };

  const clientOptions = clients.map((client) => (
    <option key={client.id} value={client.id}>
      {client.name}
    </option>
  ));
  const errorLine = formError.length > 0 ? <FieldError>{formError}</FieldError> : null;

  const identification = (keyPlaceholder: string): JSX.Element => (
    <Fieldset title="Identification">
      <Fields2>
        <Field label="Clé" required>
          <Input
            className="font-mono"
            value={key}
            onChange={(event) => {
              setKey(event.target.value);
            }}
            placeholder={keyPlaceholder}
          />
        </Field>
        <Field label="Client propriétaire" required>
          <NativeSelect
            value={ownerClientId}
            onChange={(event) => {
              changeOwner(event.target.value);
            }}
          >
            <option value="">Choisir un client</option>
            {clientOptions}
          </NativeSelect>
        </Field>
      </Fields2>
    </Fieldset>
  );

  const renderStep = (step: UseCaseCategory | null): JSX.Element => {
    switch (step) {
      case null:
        return (
          <DialogBody>
            <CategoryOption
              title={CATEGORY_LABELS.ai}
              description="Texte, analyse de documents (PDF, images), génération d’images. Anthropic, Google."
              onSelect={() => {
                chooseCategory('ai');
              }}
            />
            <CategoryOption
              title={CATEGORY_LABELS.communication}
              description="Envoi de messages aux destinataires de vos clients, facturé au SMS. Vonage."
              onSelect={() => {
                chooseCategory('communication');
              }}
              tags={
                <>
                  <Tag>SMS</Tag>
                  <Tag>WhatsApp, bientôt</Tag>
                </>
              }
            />
          </DialogBody>
        );
      case 'ai':
        return (
          <DialogBody>
            {identification('invoice-extraction')}
            <Fieldset title="Modèle">
              <Fields2>
                <Field label="Type">
                  <NativeSelect
                    value={kind}
                    onChange={(event) => {
                      switch (event.target.value) {
                        case 'text':
                        case 'image':
                          setKind(event.target.value);
                          break;
                        default:
                          break;
                      }
                    }}
                  >
                    <option value="text">Texte</option>
                    <option value="image">Image</option>
                  </NativeSelect>
                </Field>
                <Field label="Mode d’exécution">
                  <NativeSelect
                    value={executionMode}
                    onChange={(event) => {
                      switch (event.target.value) {
                        case 'realtime':
                        case 'batch':
                          setExecutionMode(event.target.value);
                          break;
                        default:
                          break;
                      }
                    }}
                  >
                    <option value="realtime">{EXECUTION_MODE_LABELS.realtime}</option>
                    <option value="batch">{EXECUTION_MODE_LABELS.batch}</option>
                  </NativeSelect>
                </Field>
              </Fields2>
              <Field label="Modèle" required>
                <NativeSelect
                  value={modelId}
                  onChange={(event) => {
                    setModelId(event.target.value);
                  }}
                >
                  <option value="">Choisir un modèle</option>
                  {aiModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.displayName}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <CheckRow
                label="Accepte des fichiers (PDF, images)"
                checked={acceptsFiles}
                onChange={(event) => {
                  setAcceptsFiles(event.target.checked);
                }}
              />
            </Fieldset>
            <Fieldset title="Paramètres de génération">
              <Fields2>
                <Field
                  label="Longueur maximale de la réponse"
                  hint={
                    <>
                      Optionnel. Paramètre <span className="font-mono">maxOutputTokens</span>.
                    </>
                  }
                >
                  <InputGroup suffix="tokens">
                    <Input
                      numeric
                      value={maxOutputTokens}
                      onChange={(event) => {
                        setMaxOutputTokens(event.target.value);
                      }}
                      placeholder="4096"
                      inputMode="numeric"
                    />
                  </InputGroup>
                </Field>
                <Field
                  label="Température"
                  hint={
                    <>
                      Optionnel, de 0 à 2. Plus la valeur est basse, plus les réponses sont stables.
                      Paramètre <span className="font-mono">temperature</span>.
                    </>
                  }
                >
                  <Input
                    numeric
                    value={temperature}
                    onChange={(event) => {
                      setTemperature(event.target.value);
                    }}
                    placeholder="0.2"
                    inputMode="decimal"
                  />
                </Field>
              </Fields2>
            </Fieldset>
            {errorLine}
          </DialogBody>
        );
      case 'communication':
        return (
          <DialogBody>
            <div className="grid gap-1.5">
              <Label id="sms-channel-label">Canal</Label>
              <div>
                <Tabs value="sms">
                  <TabsList aria-labelledby="sms-channel-label">
                    <TabsTrigger value="sms">SMS</TabsTrigger>
                    <TabsTrigger value="whatsapp" disabled>
                      WhatsApp, bientôt
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>
            {identification('sms.notification')}
            <Fieldset title="Envoi">
              <Field label="Service SMS" required>
                <NativeSelect
                  value={modelId}
                  onChange={(event) => {
                    setModelId(event.target.value);
                  }}
                  disabled={smsServices.length === 0}
                >
                  <option value="">Choisir un service</option>
                  {smsServices.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.displayName}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              {smsServices.length === 0 ? (
                <Callout variant="warning">
                  Aucun service SMS au catalogue : ajoutez un service Vonage (capacité « sms ») et
                  son tarif dans Catalogue.
                </Callout>
              ) : null}
              <Fields2>
                <SenderField value={sender} onChange={changeSender} />
                <CallingCodesField value={callingCodes} onChange={setCallingCodes} />
              </Fields2>
            </Fieldset>
            <Fieldset title="Tarification">
              <Field
                label="Prix de vente par SMS HT"
                required
                hint="Devient la règle de prix par défaut (par SMS), en vigueur dès la création."
              >
                <InputGroup suffix="€" className="max-w-60">
                  <Input
                    numeric
                    value={smsPrice}
                    onChange={(event) => {
                      setSmsPrice(event.target.value);
                    }}
                    placeholder="0.09"
                    inputMode="decimal"
                  />
                </InputGroup>
              </Field>
              {smsServices.length === 0 ? null : (
                <SmsCostLines modelId={modelId} price={smsPrice} />
              )}
            </Fieldset>
            <Fieldset title="Aperçu d’un envoi">
              <SmsSimulator pricePerSmsMicroEur={euroInputToMicroEur(smsPrice)} />
            </Fieldset>
            {errorLine}
          </DialogBody>
        );
      default:
        return assertNever(step);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <DialogContent width={640}>
        <DialogHead
          title={category === null ? 'Créer un cas d’usage' : CREATE_STEPS[category].title}
          description={
            category === null ? 'Choisir une catégorie.' : CREATE_STEPS[category].description
          }
          extra={<Stepper step={category === null ? 1 : 2} total={2} />}
        />
        {renderStep(category)}
        <DialogFooter>
          {category === null ? (
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
          ) : (
            <>
              <Button type="button" variant="ghost" className="mr-auto" onClick={back}>
                <ArrowLeft aria-hidden="true" />
                Retour
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button type="button" size="lg" onClick={submit} disabled={createUseCase.isPending}>
                Créer le cas d’usage
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
